import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, ExternalLink, FileCode2, X, Zap } from "lucide-react";
import tableauLogo from "../../assets/db_logos/tableau_logo.png";

// ─── XML escape helper ────────────────────────────────────────────────────────
function escXml(str) {
  return String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// ─── CSV escape helper ────────────────────────────────────────────────────────
function escCsv(val) {
  const s = String(val ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// ─── Shared data extractor ────────────────────────────────────────────────────
function extractWidgetData(widgets) {
  return widgets
    .map((w, wIdx) => {
      const rawName = (w.title || `Widget_${wIdx + 1}`)
        .replace(/[^a-zA-Z0-9 _]/g, "")
        .trim()
        .replace(/\s+/g, "_")
        .slice(0, 40) || `widget_${wIdx}`;

      let columns = [];
      let rows = [];

      if (
        w.expected_shape === "detail_table" &&
        Array.isArray(w.table_data) &&
        w.table_data.length
      ) {
        columns = Object.keys(w.table_data[0]).map(
          (c) =>
            c.replace(/[^a-zA-Z0-9 _]/g, "").trim() ||
            `col_${Math.random().toString(36).slice(2, 6)}`
        );
        rows = w.table_data.map((row) =>
          Object.values(row).map((v) => String(v ?? ""))
        );
      } else if (w.chart_data) {
        const cd = w.chart_data;
        const labels = cd.labels || [];
        if (cd.datasets?.length) {
          columns = [
            "Category",
            ...cd.datasets.map(
              (d, i) =>
                (d.label || `Series_${i + 1}`)
                  .replace(/[^a-zA-Z0-9 _]/g, "")
                  .trim() || `Series_${i + 1}`
            ),
          ];
          rows = labels.map((lbl, i) => [
            String(lbl),
            ...cd.datasets.map((d) => String(d.data?.[i] ?? "0")),
          ]);
        } else if (labels.length && cd.values) {
          columns = ["Category", "Value"];
          rows = labels.map((lbl, i) => [
            String(lbl),
            String(cd.values[i] ?? "0"),
          ]);
        }
      } else if (
        w.expected_shape === "single_value" ||
        w.expected_shape === "metric_with_trend" ||
        w.expected_shape === "kpi"
      ) {
        columns = ["Metric", "Value", "Comparison", "Delta_Pct"];
        rows = [
          [
            String(w.title || ""),
            String(w.display_value ?? w.value ?? ""),
            String(
              w.comparison_display_value ?? w.comparison_value ?? ""
            ),
            String(w.delta_percent ?? ""),
          ],
        ];
      }

      return { rawName, columns, rows, widget: w };
    })
    .filter((d) => d.columns.length > 0);
}

// ─── Tableau TWB Generator ────────────────────────────────────────────────────
//
//  Validated against Tableau's internal DTD. Key rules:
//
//  DATASOURCE
//    <datasource inline="true" name="...">
//      <connection class="textscan" filename="" separator=",">
//        <relation name="..." type="text">CSV_DATA_HERE</relation>
//      </connection>
//      <aliases enabled="yes"/>
//      <column datatype="..." name="[col]" role="..." type="..."/>
//    </datasource>
//
//  WORKSHEET → TABLE children order (must be exact):
//    view → style → panes → rows → cols
//
//  PANE content model: (view, mark, mark-sizing?, encodings?, ...)
//    • <breakdown value="auto"/> MUST live inside <pane><view>, NOT in <pane> directly
//    • <mark type="Bar"/> is a direct child of <pane>
//    • There is NO <mark-layout> element — that was causing the DTD errors
//
//  ROWS / COLS
//    Dimensions: [FieldName]
//    Measures:   SUM([FieldName])   ← aggregation wrapper required
//
function buildTableauWorkbook(displayDashboard, metadata) {
  const title = metadata?.title || "Dashboard";
  const widgets = displayDashboard?.widgets || [];
  const widgetData = extractWidgetData(widgets);

  if (widgetData.length === 0) {
    return `<?xml version='1.0' encoding='utf-8' ?>
<workbook source-build='2024.1.0' source-platform='win' version='18.1'
          xmlns:user='http://www.tableausoftware.com/xml/user'>
  <preferences/>
  <datasources/>
  <worksheets/>
</workbook>`;
  }

  // ── 1. Classify columns ───────────────────────────────────────────────────
  function colMeta(col, colIdx, rows) {
    const firstVal = rows[0]?.[colIdx] ?? "";
    const isNum =
      colIdx > 0 &&
      firstVal !== "" &&
      !isNaN(parseFloat(firstVal)) &&
      isFinite(Number(firstVal));
    return {
      name: col,
      fieldName: `[${col}]`,
      datatype: isNum ? "real" : "string",
      role: isNum ? "measure" : "dimension",
      type: isNum ? "quantitative" : "nominal",
      isNum,
    };
  }

  // ── 2. Datasource blocks ──────────────────────────────────────────────────
  //   Use class="textscan" with inline CSV inside <relation type="text">.
  //   This is the only inline-data format Tableau TWB supports natively.
  const datasourceBlocks = widgetData.map(({ rawName, columns, rows }) => {
    const metas = columns.map((c, i) => colMeta(c, i, rows));

    // Build inline CSV  (header + data rows)
    const csvHeader = columns.map(escCsv).join(",");
    const csvRows = rows
      .map((row) => row.map((v) => escCsv(v)).join(","))
      .join("\n");
    const colDefs = metas
      .map(
        (m) =>
          `    <column caption="${escXml(m.name)}" datatype="${m.datatype}" ` +
          `name="${m.fieldName}" role="${m.role}" type="${m.type}"/>`
      )
      .join("\n");

    return (
      `  <datasource caption="${escXml(rawName)}" inline="true" name="${escXml(rawName)}">\n` +
      `    <connection class="textscan" filename="" separator=",">\n` +
      `      <relation name="${escXml(rawName)}" type="text"><![CDATA[${csvHeader}\n${csvRows}]]></relation>\n` +
      `    </connection>\n` +
      `    <aliases enabled="yes"/>\n` +
      `${colDefs}\n` +
      `  </datasource>`
    );
  });

  // ── 3. Worksheet blocks ───────────────────────────────────────────────────
  const worksheetBlocks = widgetData.map(({ rawName, columns, rows: wRows }) => {
    const metas = columns.map((c, i) => colMeta(c, i, wRows));

    // Find dimension & measure
    const dimMeta = metas.find((m) => !m.isNum);
    const measMeta = metas.find((m) => m.isNum);

    const rowsShelf = dimMeta ? dimMeta.fieldName : "[Category]";
    const colsShelf = measMeta ? `SUM(${measMeta.fieldName})` : "SUM([Value])";

    // Column definitions
    const depColDefs = metas
      .map(
        (m) =>
          `          <column caption="${escXml(m.name)}" datatype="${m.datatype}" ` +
          `name="${m.fieldName}" role="${m.role}" type="${m.type}"/>`
      )
      .join("\n");

    return (
      `  <worksheet name="${escXml(rawName)}">\n` +
      `    <table>\n` +
      `      <view>\n` +
      `        <datasources>\n` +
      `          <datasource caption="${escXml(rawName)}" name="${escXml(rawName)}"/>\n` +
      `        </datasources>\n` +
      `        <datasource-dependencies datasource="${escXml(rawName)}">\n` +
      `${depColDefs}\n` +
      `        </datasource-dependencies>\n` +
      `        <aggregation value="true"/>\n` +
      `      </view>\n` +
      `      <style/>\n` +
      `      <panes>\n` +
      `        <pane>\n` +
      `          <view>\n` +
      `            <breakdown value="auto"/>\n` +
      `          </view>\n` +
      `          <mark class="Automatic"/>\n` +   // ✅ FIXED (critical)
      `        </pane>\n` +
      `      </panes>\n` +
      `      <rows>${escXml(rowsShelf)}</rows>\n` +
      `      <cols>${escXml(colsShelf)}</cols>\n` +
      `    </table>\n` +
      `  </worksheet>`
    );
  });

  // ── 4. Dashboard block ────────────────────────────────────────────────────
  //   Outer zone type="layout-basic"; worksheet zones have NO type attribute.
  const zoneH = 250;
  const zoneW = 580;
  const numCols = 2;
  const dashH =
    Math.ceil(widgetData.length / numCols) * (zoneH + 10) + 40;

  const zones = widgetData
    .map(({ rawName }, i) => {
      const col = i % numCols;
      const row = Math.floor(i / numCols);
      const x = col * (zoneW + 10);
      const y = row * (zoneH + 10);
      return (
        `        <zone h="${zoneH}" id="${i + 2}" ` +
        `name="${escXml(rawName)}" w="${zoneW}" x="${x}" y="${y}"/>`
      );
    })
    .join("\n");

  const dashboardBlock =
    widgetData.length > 0
      ? `  <dashboards>\n` +
        `    <dashboard name="${escXml(title)}">\n` +
        `      <style/>\n` +
        `      <size maxheight="4000" maxwidth="4000" minheight="400" minwidth="600"/>\n` +
        `      <zones>\n` +
        `        <zone id="1" type="layout-basic" x="0" y="0" ` +
        `w="${numCols * (zoneW + 10)}" h="${dashH}">\n` +
        `${zones}\n` +
        `        </zone>\n` +
        `      </zones>\n` +
        `    </dashboard>\n` +
        `  </dashboards>`
      : "";

  // ── 5. Assemble final workbook ────────────────────────────────────────────
  return (
    `<?xml version='1.0' encoding='utf-8' ?>\n` +
    `<workbook source-build='2024.1.0' source-platform='win' version='18.1'\n` +
    `          xmlns:user='http://www.tableausoftware.com/xml/user'>\n` +
    `  <preferences>\n` +
    `    <color-palette name='Dashboard' type='regular'>\n` +
    `      <color>#3B82F6</color>\n` +
    `      <color>#8B5CF6</color>\n` +
    `      <color>#10B981</color>\n` +
    `      <color>#F59E0B</color>\n` +
    `      <color>#EF4444</color>\n` +
    `      <color>#06B6D4</color>\n` +
    `    </color-palette>\n` +
    `  </preferences>\n` +
    `  <datasources>\n` +
    datasourceBlocks.join("\n") +
    `\n  </datasources>\n` +
    `  <worksheets>\n` +
    worksheetBlocks.join("\n") +
    `\n  </worksheets>\n` +
    dashboardBlock +
    (dashboardBlock ? "\n" : "") +
    `</workbook>`
  );
}

// ─── Power BI Template Generator ─────────────────────────────────────────────
//
//  Generates a Power BI Template JSON (.pbit inner DataModelSchema).
//  Power BI Desktop: File → Import → Power BI template → select the .json file.
//  The M (Power Query) expressions embed all data directly so no live
//  data source connection is required on first open.
//
function buildPowerBITemplate(displayDashboard, metadata) {
  const title = metadata?.title || "Dashboard";
  const generatedAt = metadata?.generated_at || new Date().toISOString();
  const widgets = displayDashboard?.widgets || [];
  const widgetData = extractWidgetData(widgets);

  // Build M query that embeds data as a literal table
  function buildMQuery(tableName, columns, rows) {
    const typeMap = columns.map((col, i) => {
      const firstVal = rows[0]?.[i] ?? "";
      const isNum =
        i > 0 &&
        firstVal !== "" &&
        !isNaN(parseFloat(firstVal)) &&
        isFinite(Number(firstVal));
      return { col, isNum };
    });

    const recordRows = rows
      .map((row) => {
        const fields = typeMap.map(({ col, isNum }, i) => {
          const raw = String(row[i] ?? "");
          const val = isNum ? (isNaN(Number(raw)) ? "null" : raw) : JSON.stringify(raw);
          return `#"${col}" = ${val}`;
        });
        return `        [ ${fields.join(", ")} ]`;
      })
      .join(",\n");

    const typeTransforms = typeMap
      .map(({ col, isNum }) => `{"${col}", type ${isNum ? "number" : "text"}}`)
      .join(", ");

    return (
      `let\n` +
      `    Source = #table(\n` +
      `        type table [${typeMap.map(({ col, isNum }) => `#"${col}" = ${isNum ? "number" : "text"}`).join(", ")}],\n` +
      `        {\n` +
      `${recordRows}\n` +
      `        }\n` +
      `    ),\n` +
      `    #"Changed Type" = Table.TransformColumnTypes(Source, {${typeTransforms}})\n` +
      `in\n` +
      `    #"Changed Type"`
    );
  }

  const tables = widgetData.map(({ rawName, columns, rows }) => ({
    name: rawName,
    columns,
    rows,
    mQuery: buildMQuery(rawName, columns, rows),
  }));

  const template = {
    version: "1.0",
    culture: "en-US",
    model: {
      annotations: [
        { name: "PBIDesktopVersion", value: "2.130.754.0 (24.09)" },
        {
          name: "PBI_QueryOrder",
          value: JSON.stringify(tables.map((t) => t.name)),
        },
      ],
      dataAccessOptions: {
        legacyRedirects: true,
        returnErrorValuesAsNull: true,
      },
      defaultPowerBIDataSourceVersion: "powerBI_V3",
      sourceQueryCulture: "en-US",
      tables: tables.map((t) => ({
        name: t.name,
        annotations: [
          { name: "PBI_ResultType", value: "Table" },
          { name: "PBI_NavigationStepName", value: "Navigation" },
        ],
        columns: t.columns.map((col, i) => {
          const firstVal = t.rows[0]?.[i] ?? "";
          const isNum =
            i > 0 &&
            firstVal !== "" &&
            !isNaN(parseFloat(firstVal)) &&
            isFinite(Number(firstVal));
          return {
            name: col,
            dataType: isNum ? "double" : "string",
            isHidden: false,
            sourceColumn: col,
            annotations: [
              { name: "SummarizationSetBy", value: "Automatic" },
            ],
          };
        }),
        partitions: [
          {
            name: `${t.name}-Partition`,
            mode: "import",
            source: {
              type: "m",
              expression: t.mQuery,
            },
          },
        ],
      })),
      relationships: [],
    },
    config: JSON.stringify({
      version: "5.54",
      themeCollection: {
        baseTheme: { name: "CY24SU10", version: "5.54", type: 2 },
      },
    }),
    label: title,
    description: `AI-generated dashboard: ${title}\nGenerated: ${generatedAt}\nQuery: ${metadata?.question || ""}`,
    reportPages: [
      {
        name: "ReportSection",
        displayName: title,
        ordinal: 0,
        visualContainers: tables.map((t, i) => {
          const isKpi = t.columns.length <= 4 && t.rows.length === 1;
          const visualType = isKpi ? "card" : t.columns.length === 2 ? "barChart" : "tableEx";
          return {
            x: (i % 2) * 370,
            y: Math.floor(i / 2) * 290,
            z: i,
            width: 350,
            height: 270,
            config: JSON.stringify({
              name: `visual_${i}`,
              visualType,
              projections: {
                Category: t.columns
                  .filter((_, ci) => ci === 0)
                  .map((col) => ({ queryRef: `${t.name}.${col}` })),
                Y: t.columns
                  .filter((_, ci) => ci > 0)
                  .map((col) => ({ queryRef: `${t.name}.${col}` })),
              },
              prototypeQuery: {
                Version: 2,
                From: [
                  {
                    Name: t.name.toLowerCase(),
                    Entity: t.name,
                    Type: 0,
                  },
                ],
                Select: t.columns.map((col) => ({
                  Column: {
                    Expression: {
                      SourceRef: { Source: t.name.toLowerCase() },
                    },
                    Property: col,
                  },
                  Name: `${t.name}.${col}`,
                })),
              },
            }),
            filters: "[]",
            dataTransforms: JSON.stringify({
              selects: t.columns.map((col) => ({
                displayName: col,
                queryName: `${t.name}.${col}`,
              })),
            }),
          };
        }),
      },
    ],
  };

  return JSON.stringify(template, null, 2);
}

// ─── File download trigger ────────────────────────────────────────────────────
function triggerDownload(content, filename, mimeType) {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Logo Components ──────────────────────────────────────────────────────────
function PowerBILogo() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="6" fill="#F2C811"/>
      <rect x="8" y="18" width="4" height="8" rx="1" fill="#1a1a2e"/>
      <rect x="14" y="12" width="4" height="14" rx="1" fill="#1a1a2e"/>
      <rect x="20" y="6" width="4" height="20" rx="1" fill="#1a1a2e"/>
    </svg>
  );
}

function TableauLogo() {
  return (
    <img
      src={tableauLogo}
      alt="Tableau"
      width="32"
      height="32"
      style={{ objectFit: "contain", display: "block" }}
    />
  );
}

// ─── Export Modal ─────────────────────────────────────────────────────────────
const STEPS = { PICK: "pick", PREVIEW: "preview", DONE: "done" };

export default function ExportModal({ displayDashboard, metadata, onClose }) {
  const [step, setStep] = useState(STEPS.PICK);
  const [target, setTarget] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleOverlayClick = useCallback(
    (e) => { if (e.target === overlayRef.current) onClose(); },
    [onClose]
  );

  const widgets = displayDashboard?.widgets || [];
  const kpiCount = widgets.filter((w) =>
    ["single_value", "metric_with_trend", "kpi"].includes(w.expected_shape)
  ).length;
  const chartCount = widgets.filter(
    (w) =>
      w.chart_data &&
      !["single_value", "metric_with_trend", "kpi", "detail_table"].includes(
        w.expected_shape
      )
  ).length;
  const tableCount = widgets.filter(
    (w) => w.expected_shape === "detail_table"
  ).length;

  const handleSelect = (t) => { setTarget(t); setStep(STEPS.PREVIEW); };

  const handleDownload = useCallback(() => {
    if (!displayDashboard || !target) return;
    setDownloading(true);
    try {
      const safeTitle = (metadata?.title || "dashboard")
        .replace(/[^a-zA-Z0-9_\- ]/g, "")
        .trim()
        .replace(/\s+/g, "_");

      if (target === "powerbi") {
        const content = buildPowerBITemplate(displayDashboard, metadata);
        triggerDownload(
          content,
          `${safeTitle}_powerbi_template.json`,
          "application/json"
        );
      } else {
        const content = buildTableauWorkbook(displayDashboard, metadata);
        triggerDownload(content, `${safeTitle}.twb`, "application/xml");
      }

      setTimeout(() => {
        setDownloading(false);
        setStep(STEPS.DONE);
      }, 600);
    } catch (e) {
      console.error("Export failed", e);
      setDownloading(false);
    }
  }, [displayDashboard, metadata, target]);

  const safeFilename = (metadata?.title || "dashboard")
    .replace(/[^a-zA-Z0-9 _-]/g, "")
    .trim()
    .replace(/\s+/g, "_");

  return (
    <div className="em-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="em-modal" role="dialog" aria-modal="true" aria-label="Export Dashboard">

        {/* Header */}
        <div className="em-header">
          <div className="em-header__left">
            <div className="em-header__icon"><Zap size={16} /></div>
            <div>
              <div className="em-header__title">Export Dashboard</div>
              <div className="em-header__sub">{metadata?.title || "Dashboard"}</div>
            </div>
          </div>
          <button type="button" className="em-close" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {/* Step: Pick platform */}
        {step === STEPS.PICK && (
          <div className="em-body">
            <p className="em-body__lead">
              Choose a BI platform. All widget data, chart structure, and layout are
              exported as a native template file ready to open directly.
            </p>

            <div className="em-summary">
              <span className="em-summary__chip"><strong>{kpiCount}</strong> KPIs</span>
              <span className="em-summary__chip"><strong>{chartCount}</strong> Charts</span>
              <span className="em-summary__chip"><strong>{tableCount}</strong> Tables</span>
              <span className="em-summary__chip"><strong>{widgets.length}</strong> Total widgets</span>
            </div>

            <div className="em-cards">
              <button
                type="button"
                className="em-card em-card--powerbi"
                onClick={() => handleSelect("powerbi")}
              >
                <div className="em-card__logo"><PowerBILogo /></div>
                <div className="em-card__body">
                  <div className="em-card__name">Power BI</div>
                  <div className="em-card__desc">
                    Downloads a <code>.json</code> Power BI Template with embedded M
                    queries. Open Power BI Desktop →{" "}
                    <em>File → Import → Power BI template</em>.
                  </div>
                  <div className="em-card__features">
                    <span>✓ All widget data embedded via M queries</span>
                    <span>✓ KPI cards &amp; chart visuals pre-configured</span>
                    <span>✓ No live data source required to open</span>
                  </div>
                </div>
                <div className="em-card__arrow">→</div>
              </button>

              <button
                type="button"
                className="em-card em-card--tableau"
                onClick={() => handleSelect("tableau")}
              >
                <div className="em-card__logo"><TableauLogo /></div>
                <div className="em-card__body">
                  <div className="em-card__name">Tableau</div>
                  <div className="em-card__desc">
                    Downloads a native <code>.twb</code> Tableau Workbook with inline
                    CSV data sources. Open directly in{" "}
                    <em>Tableau Desktop via File → Open</em>.
                  </div>
                  <div className="em-card__features">
                    <span>✓ Inline textscan data (no external files)</span>
                    <span>✓ Worksheets &amp; dashboard layout included</span>
                    <span>✓ Validated against Tableau DTD</span>
                  </div>
                </div>
                <div className="em-card__arrow">→</div>
              </button>
            </div>
          </div>
        )}

        {/* Step: Preview & Download */}
        {step === STEPS.PREVIEW && (
          <div className="em-body">
            <button
              type="button"
              className="em-back"
              onClick={() => setStep(STEPS.PICK)}
            >
              ← Back
            </button>

            <div className={`em-preview em-preview--${target}`}>
              <div className="em-preview__platform">
                {target === "powerbi" ? <PowerBILogo /> : <TableauLogo />}
                <span>{target === "powerbi" ? "Power BI" : "Tableau"}</span>
              </div>

              <div className="em-preview__file">
                <FileCode2 size={28} />
                <div>
                  <div className="em-preview__filename">
                    {safeFilename}
                    {target === "powerbi" ? "_powerbi_template.json" : ".twb"}
                  </div>
                  <div className="em-preview__filetype">
                    {target === "powerbi"
                      ? "Power BI Template (JSON — import via File → Import → Power BI template)"
                      : "Tableau Workbook (XML — open directly in Tableau Desktop)"}
                  </div>
                </div>
              </div>

              <div className="em-preview__contents">
                <div className="em-preview__contents-title">What's included</div>
                <div className="em-preview__items">
                  {kpiCount > 0 && (
                    <div className="em-preview__item">
                      <span className="em-preview__item-dot" />
                      KPI card data ({kpiCount} metrics)
                    </div>
                  )}
                  {chartCount > 0 && (
                    <div className="em-preview__item">
                      <span className="em-preview__item-dot" />
                      Chart datasets ({chartCount} charts with labels + series)
                    </div>
                  )}
                  {tableCount > 0 && (
                    <div className="em-preview__item">
                      <span className="em-preview__item-dot" />
                      Table data ({tableCount} detail tables, all rows)
                    </div>
                  )}
                  <div className="em-preview__item">
                    <span className="em-preview__item-dot" />
                    Metadata (title, timeframe, generated at)
                  </div>
                  {target === "tableau" && (
                    <div className="em-preview__item">
                      <span className="em-preview__item-dot" />
                      Inline textscan CSV per datasource (no external file needed)
                    </div>
                  )}
                  {target === "powerbi" && (
                    <div className="em-preview__item">
                      <span className="em-preview__item-dot" />
                      Power Query M expressions with embedded data per table
                    </div>
                  )}
                </div>
              </div>

              <div className="em-preview__steps">
                <div className="em-preview__steps-title">How to use</div>
                {target === "powerbi" ? (
                  <ol className="em-steps-list">
                    <li>Download the <code>.json</code> file below</li>
                    <li>Open <strong>Power BI Desktop</strong></li>
                    <li>
                      Go to <strong>File → Import → Power BI template</strong>
                    </li>
                    <li>Select the downloaded <code>.json</code> file</li>
                    <li>
                      All tables load automatically — connect your live database
                      to replace embedded data when ready
                    </li>
                  </ol>
                ) : (
                  <ol className="em-steps-list">
                    <li>Download the <code>.twb</code> file below</li>
                    <li>Open <strong>Tableau Desktop</strong></li>
                    <li>
                      Use <strong>File → Open</strong> and select the file
                    </li>
                    <li>
                      All worksheets and dashboard load automatically with
                      embedded data — no external file connection required
                    </li>
                    <li>
                      To use live data, go to <strong>Data → Edit Data Source</strong>
                    </li>
                  </ol>
                )}
              </div>
            </div>

            <button
              type="button"
              className={`em-download-btn em-download-btn--${target}${downloading ? " is-loading" : ""}`}
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? (
                <>
                  <span className="em-spinner" />
                  Preparing file…
                </>
              ) : (
                <>
                  <Download size={16} />
                  Download {target === "powerbi" ? "Power BI Template" : "Tableau Workbook"}
                </>
              )}
            </button>
          </div>
        )}

        {/* Step: Done */}
        {step === STEPS.DONE && (
          <div className="em-body em-body--done">
            <div className="em-done__icon">
              <CheckCircle2 size={40} />
            </div>
            <div className="em-done__title">Export complete</div>
            <div className="em-done__desc">
              Your {target === "powerbi" ? "Power BI Template" : "Tableau Workbook"} has
              been downloaded successfully.
            </div>
            <div className="em-done__actions">
              <button
                type="button"
                className="em-done__again"
                onClick={() => { setStep(STEPS.PICK); setTarget(null); }}
              >
                Export to another platform
              </button>
              <button type="button" className="em-done__close" onClick={onClose}>
                Done
              </button>
            </div>
            <div className="em-done__hint">
              <ExternalLink size={12} />
              {target === "powerbi"
                ? "Power BI Desktop → File → Import → Power BI template → select the .json file."
                : "Double-click the .twb file or use File → Open in Tableau Desktop."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
